import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import Slide from 'react-reveal/Slide';
import './styles/products.css';

const   productsData = [
    {
        id: 1,
        title: "PASADA Mobile App",
        description: "Real-time tracking and booking system for passengers to locate and reserve seats in modern jeepneys and buses.",
        features: ["Real-time GPS tracking", "Route optimization"],
        image: "https://via.placeholder.com/400x250/667eea/ffffff?text=Mobile+App",
        category: "Mobile Application",
        status: "Live"
    },
    {
        id: 2,
        title: "Driver Dashboard",
        description: "Comprehensive dashboard for drivers to manage routes, track earnings, and communicate with passengers.",
        features: ["Route management", "Performance analytics"],
        image: "https://via.placeholder.com/400x250/4facfe/ffffff?text=Driver+Dashboard",
        category: "Web Application",
        status: "Live"
    },
    {
        id: 3,
        title: "Fleet Management System",
        description: "Advanced fleet management solution for transport operators to monitor vehicle performance and optimize operations.",
        features: ["Vehicle monitoring",  "Driver management"],
        image: "https://via.placeholder.com/400x250/11998e/ffffff?text=Fleet+Management",
        category: "Enterprise Solution",
        status: "Beta"
    },
    {
        id: 4,
        title: "Passenger Mobile Application",
        description: "A user-friendly mobile app designed for passengers, providing real-time travel information and improved commuting experience.",
        features: ["Estimated Time of Arrival (ETA)", "Real-time vehicle tracking"],
        image: "https://via.placeholder.com/400x250/764ba2/ffffff?text=Passenger+App",
        category: "Mobile Application",
        status: "Coming Soon"
    },
    {
        id: 5,
        title: "Route Analytics Platform",
        description: "Data-driven insights platform for transport authorities to optimize route planning and improve service quality.",
        features: ["Traffic analysis", "Route optimization", "Performance metrics"],
        image: "https://via.placeholder.com/400x250/38ef7d/ffffff?text=Analytics+Platform",
        category: "Analytics Platform",
        status: "Development"
    },
    {
        id: 6,
        title: "Passenger Safety System",
        description: "Emergency response and safety monitoring system with panic buttons and real-time location sharing.",
        features: ["Emergency alerts", "Location sharing", "Safety monitoring"],
        image: "https://via.placeholder.com/400x250/ff6b6b/ffffff?text=Safety+System",
        category: "Safety Solution",
        status: "Coming Soon"
    }
];

const Products = () => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'Live': return 'success';
            case 'Beta': return 'warning';
            case 'Development': return 'info';
            case 'Coming Soon': return 'secondary';
            default: return 'primary';
        }
    };

    return (
        <div className="products-page">
            <Helmet>
                <title>Products - PASADA</title>
                <meta name="description" content="Explore PASADA's innovative transportation technology solutions" />
                <link rel="canonical" href="/products" />
            </Helmet>

            {/* Hero Section */}
            <section className="products-hero">
                <Container>
                    <Row className="align-items-center min-vh-60">
                        <Col lg={8} md={10} className="mx-auto text-center">
                            <Slide bottom>
                                <h1 className="hero-title">Our Products</h1>
                                <p className="hero-subtitle">
                                    Innovative technology solutions revolutionizing public transportation in the Philippines
                                </p>
                            </Slide>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Products Grid */}
            <section className="products-grid py-5">
                <Container>
                    <Row>
                        {productsData.map((product, index) => (
                            <Col lg={4} md={6} className="mb-4" key={product.id}>
                                <Slide bottom delay={index * 100}>
                                    <Card className="product-card h-100 shadow-sm">
                                        <div className="product-image-wrapper">
                                            <Card.Img 
                                                variant="top" 
                                                src={product.image} 
                                                className="product-image"
                                                alt={product.title}
                                                onError={(e) => {
                                                    e.target.src = "https://via.placeholder.com/400x250/cccccc/ffffff?text=Product+Image";
                                                }}
                                            />
                                            <div className="product-overlay">
                                                <Badge 
                                                    bg={getStatusColor(product.status)} 
                                                    className="status-badge"
                                                >
                                                    {product.status}
                                                </Badge>
                                            </div>
                                        </div>
                                        <Card.Body className="d-flex flex-column">
                                            <div className="mb-2">
                                                <Badge bg="outline-primary" className="category-badge">
                                                    {product.category}
                                                </Badge>
                                            </div>
                                            <Card.Title className="product-title">{product.title}</Card.Title>
                                            <Card.Text className="product-description">
                                                {product.description}
                                            </Card.Text>
                                            <div className="features-list mb-3">
                                                <h6>Key Features:</h6>
                                                <ul className="feature-list">
                                                    {product.features.map((feature, idx) => (
                                                        <li key={idx}>{feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="mt-auto">
                                                <Button 
                                                    variant="primary" 
                                                    className="w-100 product-btn"
                                                    disabled={product.status === 'Coming Soon'}
                                                >
                                                    {product.status === 'Live' ? 'Learn More' :
                                                     product.status === 'Beta' ? 'Join Beta' :
                                                     product.status === 'Development' ? 'Get Updates' :
                                                     'Coming Soon'}
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Slide>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* CTA Section */}
            <section className="products-cta py-5 bg-primary text-white">
                <Container>
                    <Row>
                        <Col lg={8} className="mx-auto text-center">
                            <Slide bottom>
                                <h2>Ready to Transform Transportation?</h2>
                                <p className="lead mb-4">
                                    Join thousands of drivers and passengers who are already experiencing the future of public transportation.
                                </p>
                                <Button variant="light" size="lg" className="me-3 mb-2">
                                    Get Started
                                </Button>
                                <Button variant="outline-light" size="lg">
                                    Contact Sales
                                </Button>
                            </Slide>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default Products;